
import React from 'react';
import { Link } from 'react-router-dom';
import HeaderNavigation from './header/HeaderNavigation';
import HeaderMobileMenu from './header/HeaderMobileMenu';
import UserNavigation from './header/UserNavigation';
import { AuthButtons } from './header/AuthButtons';
import { useHeaderAuth } from '@/hooks/useHeaderAuth';
import { useTaskNotifications } from '@/hooks/useTaskNotifications';
import TaskNotification from '@/components/profile/TaskNotification';
import { useAuth, isSuperAdmin } from '@/context/SupabaseAuthContext';
import { addIconsToTasks } from '@/utils/taskIcons';
import HeaderBranding from './header/HeaderBranding';

interface HeaderProps {
  isLoggedIn?: boolean;
  isAdmin?: boolean;
  userRole?: string;
  brandName?: string;
  onLogout?: () => void;
}

const Header: React.FC<HeaderProps> = ({ 
  isLoggedIn = false,
  isAdmin,
  userRole,
  brandName,
  onLogout
}) => {
  const headerAuth = useHeaderAuth();
  const { user, profile } = useAuth();
  const { tasks, markTaskComplete } = useTaskNotifications(user as any);
  
  const handleLogout = onLogout || headerAuth.handleLogout;

  // Transform tasks to include icons
  const tasksWithIcons = addIconsToTasks(tasks);

  const getDashboardLink = () => {
    if (!userRole) return '/dashboard';
    
    switch(userRole.toLowerCase()) {
      case 'admin':
        return '/admin/dashboard';
      case 'superadmin':
        return '/super-admin/dashboard';
      case 'masteradmin':
        return '/master/dashboard';
      case 'business':
      case 'manager':
        return '/business/dashboard';
      default:
        return '/user/dashboard';
    }
  };

  return (
    <header className="bg-primary-blue py-2 shadow-sm text-white">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-2">
          <HeaderBranding 
            isMasterAdmin={userRole?.toLowerCase() === 'masteradmin'} 
            isMasterLicenseRoute={false}
            brandName={brandName} 
          />

          <HeaderNavigation />

          <div className="flex items-center space-x-2">
            {isLoggedIn && (
              <TaskNotification 
                tasks={tasksWithIcons} 
                onTaskComplete={markTaskComplete} 
              />
            )}

            <HeaderMobileMenu
              isLoggedIn={isLoggedIn}
              onLogout={handleLogout}
              isUserDashboard={location.pathname.startsWith('/user/')}
              getDashboardLink={getDashboardLink}
            />
          </div>
        </div>
        
        <div className="hidden md:flex items-center justify-between border-t border-blue-400 pt-2">
          <div className="flex items-center space-x-4">
            {isLoggedIn && (
              <UserNavigation
                isUserDashboard={location.pathname.startsWith('/user/')}
                getDashboardLink={getDashboardLink}
              />
            )}
          </div>
          
          <AuthButtons isLoggedIn={isLoggedIn} />
        </div>
      </div>
    </header>
  );
};

export default Header;
